/**
 * Small ArchiMate notation glyphs rendered in the top-right corner of every
 * concept rectangle, matching the icon position in the official ArchiMate 3.2
 * Reference Cards.
 *
 * Each icon is a 16×16 SVG drawn with `currentColor` so it picks up the
 * concept node's text colour. Behaviour-shape categories (Function chevron,
 * Process arrow, Service pill, Event notch, Interaction halves, Interface
 * stem+circle, Collaboration overlapping circles) are shared across the
 * Business / Application / Technology layers — the registry pack identifies
 * each via its element-type id, but the visual glyph is identical because
 * that's how the spec defines them.
 */

import type { ReactNode } from "react";
import { getRegistry } from "../../registry";
import { getSvgByName } from "./archimateIconLibrary";

const STROKE = "currentColor";

const Wrap = ({ children }: { children: ReactNode }) => (
  <svg
    viewBox="0 0 16 16"
    width="100%"
    height="100%"
    fill="none"
    stroke={STROKE}
    strokeWidth={1.25}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);

// ---- Active-structure ------------------------------------------------------

const ActorIcon = () => (
  <Wrap>
    <circle cx={8} cy={3.5} r={1.8} />
    <path d="M8 5.5 V11 M4.5 8 H11.5 M8 11 L5 14.5 M8 11 L11 14.5" />
  </Wrap>
);

const RoleIcon = () => (
  <Wrap>
    <ellipse cx={4} cy={8} rx={2} ry={3} />
    <path d="M4 5 H12 M4 11 H12" />
    <ellipse cx={12} cy={8} rx={2} ry={3} />
  </Wrap>
);

const CollaborationIcon = () => (
  <Wrap>
    <circle cx={6} cy={8} r={3.5} />
    <circle cx={10} cy={8} r={3.5} />
  </Wrap>
);

const InterfaceIcon = () => (
  <Wrap>
    <path d="M2 8 H10" />
    <circle cx={12} cy={8} r={2} />
  </Wrap>
);

const ComponentIcon = () => (
  <Wrap>
    <rect x={5} y={3} width={9} height={10} />
    <rect x={2} y={5} width={4} height={2} />
    <rect x={2} y={9} width={4} height={2} />
  </Wrap>
);

const NodeIcon = () => (
  <Wrap>
    <rect x={2} y={5} width={10} height={8} />
    <path d="M2 5 L5 2 H15 V12 L12 13 M12 5 L15 2" />
  </Wrap>
);

const DeviceIcon = () => (
  <Wrap>
    <rect x={2} y={4} width={12} height={8} rx={1.5} />
    <path d="M5 12 L4 14 H12 L11 12" />
  </Wrap>
);

const SystemSoftwareIcon = () => (
  <Wrap>
    <circle cx={8} cy={8} r={5} />
  </Wrap>
);

const PathIcon = () => (
  <Wrap>
    <path d="M5 5 L2 8 L5 11 M11 5 L14 8 L11 11 M2 8 H14" />
  </Wrap>
);

const NetworkIcon = () => (
  <Wrap>
    <circle cx={4} cy={4} r={1.5} fill={STROKE} />
    <circle cx={12} cy={4} r={1.5} fill={STROKE} />
    <circle cx={8} cy={12} r={1.5} fill={STROKE} />
    <path d="M4 4 L12 4 M4 4 L8 12 M12 4 L8 12" />
  </Wrap>
);

const FacilityIcon = () => (
  <Wrap>
    <path d="M2 14 V8 L7 6 V8 L13 6 V14 Z M5 3 V7" />
  </Wrap>
);

const EquipmentIcon = () => (
  <Wrap>
    <circle cx={8} cy={8} r={2.5} />
    <path d="M8 2 V4 M8 12 V14 M2 8 H4 M12 8 H14 M3.5 3.5 L5 5 M11 11 L12.5 12.5 M3.5 12.5 L5 11 M11 5 L12.5 3.5" />
  </Wrap>
);

const DistributionNetworkIcon = () => (
  <Wrap>
    <path d="M5 4 L2 8 L5 12 M11 4 L14 8 L11 12 M2 8 H14" />
  </Wrap>
);

// ---- Behavior --------------------------------------------------------------

const ProcessIcon = () => (
  <Wrap>
    <path d="M2 6 H9 V4 L14 8 L9 12 V10 H2 Z" />
  </Wrap>
);

const FunctionIcon = () => (
  <Wrap>
    <path d="M3 11 L8 5 L13 11 M3 8 L8 2 L13 8" />
  </Wrap>
);

const InteractionIcon = () => (
  <Wrap>
    <circle cx={8} cy={8} r={5} />
    <path d="M8 3 V13" />
  </Wrap>
);

const EventIcon = () => (
  <Wrap>
    <path d="M2 4 H11 L14 8 L11 12 H2 L4 8 Z" />
  </Wrap>
);

const ServiceIcon = () => (
  <Wrap>
    <rect x={2} y={4} width={12} height={8} rx={4} />
  </Wrap>
);

// ---- Passive structure -----------------------------------------------------

const PassiveObjectIcon = () => (
  <Wrap>
    <rect x={2} y={4} width={12} height={8} />
    <path d="M2 6 H14" />
  </Wrap>
);

const ContractIcon = () => (
  <Wrap>
    <rect x={2} y={3} width={12} height={10} />
    <path d="M2 6 H14 M2 10 H14" />
  </Wrap>
);

const RepresentationIcon = () => (
  <Wrap>
    <path d="M3 3 H13 V11 Q8 14 3 11 Z" />
  </Wrap>
);

const ProductIcon = () => (
  <Wrap>
    <path d="M3 3 H11 L13 5 V13 H3 Z M11 3 V5 H13" />
  </Wrap>
);

const ArtifactIcon = () => (
  <Wrap>
    <path d="M3 2 H10 L13 5 V14 H3 Z M10 2 V5 H13" />
  </Wrap>
);

const MaterialIcon = () => (
  <Wrap>
    <path d="M5 3 H11 L14 8 L11 13 H5 L2 8 Z" />
  </Wrap>
);

// ---- Motivation ------------------------------------------------------------

const StakeholderIcon = () => (
  <Wrap>
    <rect x={3} y={3} width={10} height={10} />
    <path d="M3 6 H13" />
  </Wrap>
);

const DriverIcon = () => (
  <Wrap>
    <circle cx={8} cy={8} r={5} />
    <path d="M8 3 V13 M3 8 H13 M4.5 4.5 L11.5 11.5 M4.5 11.5 L11.5 4.5" />
  </Wrap>
);

const AssessmentIcon = () => (
  <Wrap>
    <circle cx={6} cy={6} r={3} />
    <path d="M8.2 8.2 L13 13" />
  </Wrap>
);

const GoalIcon = () => (
  <Wrap>
    <circle cx={8} cy={8} r={5} />
    <circle cx={8} cy={8} r={1.8} fill={STROKE} />
  </Wrap>
);

const OutcomeIcon = () => (
  <Wrap>
    <circle cx={9} cy={7} r={4} />
    <circle cx={9} cy={7} r={1.4} fill={STROKE} />
    <path d="M2 14 L9 7 M2 14 L4.5 13.5 M2 14 L2.5 11.5" />
  </Wrap>
);

const PrincipleIcon = () => (
  <Wrap>
    <path d="M8 3 V10" />
    <circle cx={8} cy={13} r={0.9} fill={STROKE} />
  </Wrap>
);

const RequirementIcon = () => (
  <Wrap>
    <path d="M5 3 H14 L11 13 H2 Z" />
  </Wrap>
);

const ConstraintIcon = () => (
  <Wrap>
    <path d="M5 3 H14 L11 13 H2 Z" />
    <path d="M5 3 L2 13 M14 3 L11 13" />
  </Wrap>
);

const MeaningIcon = () => (
  <Wrap>
    <path d="M3 6 Q3 3 6 3 H10 Q13 3 13 6 Q13 9 10 9 H7 L5 12 L5.5 9 Q3 9 3 6 Z" />
  </Wrap>
);

const ValueIcon = () => (
  <Wrap>
    <ellipse cx={8} cy={8} rx={6} ry={4} />
  </Wrap>
);

const UseCaseIcon = () => (
  <Wrap>
    <ellipse cx={8} cy={7} rx={5.5} ry={3.5} />
    <path d="M5 6.5 H11 M5 8 H9" />
  </Wrap>
);

const MetricIcon = () => (
  <Wrap>
    <path d="M2 13 V3 M2 13 H14" />
    <path d="M3 10 L6 7 L9 9 L13 4" />
  </Wrap>
);

const GuidelineIcon = () => (
  <Wrap>
    <path d="M4 2 H12 V14 H4 Z" />
    <path d="M6 5.5 L7.5 7 L6 8.5 M8 7 H11 M6 10 H11 M6 12 H9" />
  </Wrap>
);

const VulnerabilityIcon = () => (
  <Wrap>
    <path d="M8 2 L13 4.5 V8.5 Q13 13 8 14.5 Q3 13 3 8.5 V4.5 Z" />
    <path d="M8 5.5 L7 8.5 L9 8.5 L8 12" />
  </Wrap>
);

const RiskIcon = () => (
  <Wrap>
    <path d="M8 2.5 L14 13.5 H2 Z" />
    <path d="M8 6.5 V10" />
    <circle cx={8} cy={11.5} r={0.7} fill={STROKE} />
  </Wrap>
);

const ControlObjectiveIcon = () => (
  <Wrap>
    <circle cx={8} cy={8} r={5.5} />
    <circle cx={8} cy={8} r={2.5} />
    <path d="M8 2.5 V5.5 M8 10.5 V13.5 M2.5 8 H5.5 M10.5 8 H13.5" />
  </Wrap>
);

const SecurityPrincipleIcon = () => (
  <Wrap>
    <path d="M8 2 L13 4.5 V8.5 Q13 13 8 14.5 Q3 13 3 8.5 V4.5 Z" />
    <path d="M8 6 V9.5" />
    <circle cx={8} cy={11.2} r={0.65} fill={STROKE} />
  </Wrap>
);

const ControlMeasureIcon = () => (
  <Wrap>
    <path d="M8 2 L13 4.5 V8.5 Q13 13 8 14.5 Q3 13 3 8.5 V4.5 Z" />
    <path d="M5.5 8.5 L7 10.5 L11 6.5" />
  </Wrap>
);

const ProgramIcon = () => (
  <Wrap>
    <path d="M2 6 H6 L8 4 H14 V6" />
    <rect x={2} y={6} width={12} height={8} rx={1} />
    <path d="M5 10 H11 M5 12 H8.5" />
  </Wrap>
);

// ---- Strategy --------------------------------------------------------------

const ResourceIcon = () => (
  <Wrap>
    <rect x={2} y={5} width={11} height={6} />
    <path d="M5 5 V11 M8 5 V11 M11 5 V11" />
    <rect x={13} y={7} width={1.5} height={2} fill={STROKE} />
  </Wrap>
);

const CapabilityIcon = () => (
  <Wrap>
    <rect x={2} y={10} width={3} height={4} fill={STROKE} />
    <rect x={6.5} y={7} width={3} height={7} fill={STROKE} />
    <rect x={11} y={4} width={3} height={10} fill={STROKE} />
  </Wrap>
);

const ValueStreamIcon = () => (
  <Wrap>
    <path d="M2 4 H10 L13 8 L10 12 H2 L5 8 Z" />
  </Wrap>
);

const CourseOfActionIcon = () => (
  <Wrap>
    <circle cx={11} cy={5} r={2.5} />
    <path d="M9 7 L3 13 L2 14" />
    <path d="M3 11 L5 13" />
  </Wrap>
);

// ---- Implementation & Migration -------------------------------------------

const WorkPackageIcon = () => (
  <Wrap>
    <circle cx={7} cy={8} r={4} />
    <path d="M9.5 10.5 L13.5 14.5 M13.5 14.5 L11.5 14.5 M13.5 14.5 L13.5 12.5" />
  </Wrap>
);

const PlateauIcon = () => (
  <Wrap>
    <rect x={2} y={4} width={12} height={1.6} fill={STROKE} />
    <rect x={2} y={7.5} width={12} height={1.6} fill={STROKE} />
    <rect x={2} y={11} width={12} height={1.6} fill={STROKE} />
  </Wrap>
);

const GapIcon = () => (
  <Wrap>
    <circle cx={8} cy={8} r={5} />
    <path d="M2 8 H14" />
  </Wrap>
);

// ---- Composite -------------------------------------------------------------

const GroupingIcon = () => (
  <Wrap>
    <rect x={2} y={3} width={12} height={10} strokeDasharray="2 2" />
  </Wrap>
);

const LocationIcon = () => (
  <Wrap>
    <path d="M8 2 Q3 2 3 7 Q3 10 8 14 Q13 10 13 7 Q13 2 8 2 Z" />
    <circle cx={8} cy={7} r={1.4} fill={STROKE} />
  </Wrap>
);

const SecurityDomainIcon = () => (
  <Wrap>
    <rect x={2} y={3} width={12} height={10} rx={1} />
    <path d="M8 6 L11 7.5 V10 Q11 12 8 12.5 Q5 12 5 10 V7.5 Z" />
  </Wrap>
);

// ---- Mapping ---------------------------------------------------------------

const ICONS: Record<string, () => ReactNode> = {
  // Business
  "archimate.BusinessActor": ActorIcon,
  "archimate.BusinessRole": RoleIcon,
  "archimate.BusinessCollaboration": CollaborationIcon,
  "archimate.BusinessInterface": InterfaceIcon,
  "archimate.BusinessProcess": ProcessIcon,
  "archimate.BusinessFunction": FunctionIcon,
  "archimate.BusinessInteraction": InteractionIcon,
  "archimate.BusinessEvent": EventIcon,
  "archimate.BusinessService": ServiceIcon,
  "archimate.BusinessObject": PassiveObjectIcon,
  "archimate.Contract": ContractIcon,
  "archimate.Representation": RepresentationIcon,
  "archimate.Product": ProductIcon,
  // Application
  "archimate.ApplicationComponent": ComponentIcon,
  "archimate.ApplicationCollaboration": CollaborationIcon,
  "archimate.ApplicationInterface": InterfaceIcon,
  "archimate.ApplicationFunction": FunctionIcon,
  "archimate.ApplicationInteraction": InteractionIcon,
  "archimate.ApplicationProcess": ProcessIcon,
  "archimate.ApplicationEvent": EventIcon,
  "archimate.ApplicationService": ServiceIcon,
  "archimate.DataObject": PassiveObjectIcon,
  // Technology
  "archimate.Node": NodeIcon,
  "archimate.Device": DeviceIcon,
  "archimate.SystemSoftware": SystemSoftwareIcon,
  "archimate.TechnologyCollaboration": CollaborationIcon,
  "archimate.TechnologyInterface": InterfaceIcon,
  "archimate.Path": PathIcon,
  "archimate.CommunicationNetwork": NetworkIcon,
  "archimate.TechnologyFunction": FunctionIcon,
  "archimate.TechnologyProcess": ProcessIcon,
  "archimate.TechnologyInteraction": InteractionIcon,
  "archimate.TechnologyEvent": EventIcon,
  "archimate.TechnologyService": ServiceIcon,
  "archimate.Artifact": ArtifactIcon,
  "archimate.Equipment": EquipmentIcon,
  "archimate.Facility": FacilityIcon,
  "archimate.DistributionNetwork": DistributionNetworkIcon,
  "archimate.Material": MaterialIcon,
  // Motivation
  "archimate.Stakeholder": StakeholderIcon,
  "archimate.Driver": DriverIcon,
  "archimate.Assessment": AssessmentIcon,
  "archimate.Goal": GoalIcon,
  "archimate.Outcome": OutcomeIcon,
  "archimate.Principle": PrincipleIcon,
  "archimate.Requirement": RequirementIcon,
  "archimate.Constraint": ConstraintIcon,
  "archimate.Meaning": MeaningIcon,
  "archimate.Value": ValueIcon,
  "archimate.UseCase": UseCaseIcon,
  "archimate.Metric": MetricIcon,
  "archimate.Guideline": GuidelineIcon,
  // Strategy
  "archimate.Resource": ResourceIcon,
  "archimate.Capability": CapabilityIcon,
  "archimate.ValueStream": ValueStreamIcon,
  "archimate.CourseOfAction": CourseOfActionIcon,
  // Implementation & Migration
  "archimate.WorkPackage": WorkPackageIcon,
  "archimate.Deliverable": RepresentationIcon,
  "archimate.ImplementationEvent": EventIcon,
  "archimate.Program": ProgramIcon,
  "archimate.Plateau": PlateauIcon,
  "archimate.Gap": GapIcon,
  // Composite
  "archimate.Grouping": GroupingIcon,
  "archimate.Location": LocationIcon,
  "archimate.SecurityDomain": SecurityDomainIcon,
  // C4-extensions hergebruiken parent-type icons (zelfde visuele
  // betekenis: c4.Person ≈ BusinessActor stick-figure;
  // c4.SoftwareSystemInstance / c4.ContainerInstance ≈ ApplicationComponent;
  // c4.InfrastructureNode ≈ Node). Voor C4-specifieke icon-style kan
  // dit later vervangen worden door SVG's uit
  // https://github.com/rabidgremlin/c4model-svg of vergelijkbaar —
  // dan lookup via registry icon_id i.p.v. typeId.
  "c4.Person": ActorIcon,
  "c4.SoftwareSystemInstance": ComponentIcon,
  "c4.ContainerInstance": ComponentIcon,
  "c4.InfrastructureNode": NodeIcon,

  // ArchiMate 4 Common Domain — de generieke types waarin de
  // laag-gebonden 3.1-varianten samenvallen. De icoon-componenten zijn
  // al laag-onafhankelijk (BusinessProcess en ApplicationProcess deelden
  // ProcessIcon), dus dit is hergebruik.
  "archimate.Process": ProcessIcon,
  "archimate.Function": FunctionIcon,
  "archimate.Service": ServiceIcon,
  "archimate.Event": EventIcon,
  "archimate.Collaboration": CollaborationIcon,
  "archimate.Role": RoleIcon,
};

export function getElementIcon(typeId: string): ReactNode | null {
  const C = ICONS[typeId];
  return C ? <C /> : null;
}

/** Stereotype-aware icon-lookup. De risk-overlay-refactor (2026-05-17)
 *  verplaatste Vulnerability/Risk/ControlObjective/SecurityPrinciple/
 *  ControlMeasure van eigen ArchiMate-types naar stereotypes op
 *  Assessment / Goal / Requirement. De iconografie hoort echter nog
 *  bij het *stereotype*, niet bij de base. Deze helper geeft de eerste
 *  matchende icoon terug zodat een Assessment met «Risk»-stereotype
 *  alsnog de Risk-icoon-decoratie krijgt.
 *
 *  ThreatEvent / ThreatActor / LossEvent hebben nog geen eigen SVG —
 *  voor die stereotypes returnt deze helper `null` totdat we icons
 *  toevoegen. */
const STEREOTYPE_ICONS: Record<string, () => ReactNode> = {
  "risk.Vulnerability": VulnerabilityIcon,
  "risk.Risk": RiskIcon,
  "risk.ControlObjective": ControlObjectiveIcon,
  "risk.SecurityPrinciple": SecurityPrincipleIcon,
  "risk.Countermeasure": ControlMeasureIcon,

  // ArchiMate 3.1-compat (fase 2 van de ArchiMate 4-migratie). Alleen de
  // vier gevallen waar het merge-doel een ANDER icoon heeft dan het oude
  // type — zonder deze override krijgt een gemigreerd «Gap» het
  // Assessment-icoon. Spiegelt STEREOTYPE_TO_SVG in
  // archimateIconLibrary.ts; houd beide in sync.
  "archimate31.Constraint": ConstraintIcon,
  "archimate31.Contract": ContractIcon,
  "archimate31.Gap": GapIcon,
  "archimate31.Representation": RepresentationIcon,
};

export function getStereotypeIcon(stereotypes: readonly string[]): ReactNode | null {
  for (const s of stereotypes) {
    const C = STEREOTYPE_ICONS[s];
    if (C) return <C />;
  }
  // Zelf gedefinieerde profielen staan niet in de map hierboven — die wordt
  // bij het bouwen vastgelegd en hun definitie leeft in D1. Ze wijzen hun
  // icoon aan met `icon_id`, uit dezelfde meegeleverde set.
  for (const s of stereotypes) {
    const iconId = getRegistry().getStereotype(s)?.icon_id;
    if (!iconId) continue;
    const svg = getSvgByName(iconId);
    if (!svg) continue;
    // De SVG's komen uit de repo en worden bij het bouwen meegebundeld — geen
    // gebruikersinvoer. Zou hier ooit een upload landen, dan moet er eerst
    // gesaneerd worden: een SVG kan script dragen.
    return (
      <span
        aria-hidden
        className="inline-block h-full w-full"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }
  return null;
}
